/**
 * The Agentic Readiness Index — six dimensions, eighteen questions.
 *
 * Kagan's tool, handed over 2026-08-25. The last of the three ported, and the
 * largest: six dimensions of three questions, five options each, four
 * archetypes, a five-rung ladder and a routing table onto Digitopia's own
 * three offerings.
 *
 * Extracted from the source programmatically, same as the Diagnostic — this is
 * far too much prose to retype without losing something.
 *
 * ---------------------------------------------------------------------------
 * THE TWO SIDES ARE THE POINT.
 *
 * Three dimensions are `hub` — value, trust, change — and describe what the
 * BUSINESS has authorised. Three are `core` — knowledge, systems, operate — and
 * describe what the SYSTEMS can actually do. The archetype is the pair, split at
 * 2.5:
 *
 *              core low        core high
 *   hub high   ambition-led    compounding
 *   hub low    observer        capability-led
 *
 * Which is the same argument the Diagnostic makes about will and skill, and it
 * is why neither tool reports one number as its headline. The overall `ari` mean
 * exists, but it is the third thing on the page, not the first.
 *
 * Options are worth their index PLUS ONE — 1 to 5, not 0 to 4 — so an
 * unanswered question defaults to 1 rather than to nothing. Kept from the
 * source: it means a dimension score is always on the same 1–5 scale as the
 * ladder, and the ladder rung is `round(score) - 1`.
 * ---------------------------------------------------------------------------
 */

export const ari = {
  ui: {
    hubLabel: 'Hub',
    coreLabel: 'Core',
    hubBlurb:
      'What the business has authorised: whether there is a named unit of value, how much you would let an agent decide, and whether the organisation can absorb the change.',
    coreBlurb:
      'What the systems can actually do: whether an agent can reach governed knowledge, act inside core systems, and be operated and proven once it is live.',
    indexLabel: 'Index',
    contextHeading: 'Two things before we start',
    contextSub:
      'Neither is required, and neither affects your score. They change what the follow-up is worth: an agent that has to reach SAP is a different problem from one that has to reach a spreadsheet.',
    sectorLabel: 'Sector',
    sectorPlaceholder: 'Prefer not to say',
    systemsLabel: 'Core systems you actually run',
    systemsSub: 'Pick any that apply, or none.',
    yourArchetype: 'Your archetype',
    yourMove: 'Your move',
    blindHeading: 'What is missing outright',
    blindSub:
      'Components you placed at the bottom of the scale. These are not weaknesses to improve; they are things that do not exist yet, and each one blocks everything above it.',
    routedHeading: 'Where we would start',
    routedSub: 'Based on your three weakest dimensions, not on your archetype.',
    ctaHeading: 'Want this run against your own numbers rather than your impressions?',
  },
  ladder: [
    {
      n: 1,
      label: 'Unaware',
      band: 'Awareness',
      desc: 'Not on the agenda. The question itself is new.',
    },
    {
      n: 2,
      label: 'Aware',
      band: 'Awareness',
      desc: 'Recognised as something that matters. No decision made.',
    },
    {
      n: 3,
      label: 'Deliberate',
      band: 'Readiness',
      desc: 'Decided, documented, owned. Partially in place.',
    },
    {
      n: 4,
      label: 'Operational',
      band: 'Readiness',
      desc: 'Live in production and working.',
    },
    {
      n: 5,
      label: 'Systemic',
      band: 'Readiness',
      desc: 'Governed, measured, improving by default, enterprise-wide.',
    },
  ],
  sectors: [
    'Banking',
    'Insurance',
    'Manufacturing',
    'Automotive',
    'Consumer products',
    'Retail',
    'Energy',
    'Utilities & infrastructure',
    'Logistics & transport',
    'Pharmaceuticals',
    'Healthcare',
    'Technology',
    'Telecommunications',
    'Construction',
    'Airlines',
    'Airports',
    'Tourism & hospitality',
    'Professional services',
    'Public sector',
    'Other',
  ],
  systems: [
    'SAP',
    'Oracle',
    'IFS',
    'Workday',
    'Salesforce',
    'Microsoft Dynamics',
    'ServiceNow',
    'Infor',
    'Epicor',
    'Sage',
    'Other',
    'Not sure',
  ],
  dims: [
    {
      id: 'value',
      side: 'hub',
      title: 'Value & Ambition',
      sub: 'Is there a defined unit of value?',
      intro: 'Agentic AI only pays when it is pointed at a named, measured business outcome. This dimension checks whether that outcome exists.',
      questions: [
        {
          component: 'A defined unit of value',
          text: 'When your organisation talks about agentic AI, what is actually being discussed?',
          opts: [
            'Tools and pilots: we’re trying copilots and seeing what sticks',
            'A list of candidate use cases, with no value attached to them',
            'Named business outcomes, though not all of them are quantified',
            'Outcomes with a baseline, a target and a named owner in the business',
            'A managed portfolio of outcomes, re-prioritised as evidence arrives',
          ],
        },
        {
          component: 'Baseline, target and business owner',
          text: 'For your highest-priority agentic initiative, what exists today?',
          opts: [
            'None of the below; it hasn’t been framed in value terms',
            'A qualitative benefit case (“faster”, “better service”, “efficiency”)',
            'A quantified target, but no measured baseline to compare it against',
            'Baseline, target, and an accountable owner outside IT',
            'All of that, plus a P&L line the CFO recognises as their own',
          ],
        },
        {
          component: 'Evidence-based prioritisation',
          text: 'How do you decide which agentic opportunities go first?',
          opts: [
            'Whoever asks loudest, or whatever the last vendor demoed',
            'IT’s judgement about what is technically feasible',
            'A structured value-versus-effort screen',
            'Value and effort screened against measured capability gaps',
            'Continuously re-scored as outcome evidence comes back in',
          ],
        },
      ],
      next: [
        'Name one outcome, in business language, that you want an agent to move. Not “deploy copilots” but “cut claim handling time by 30%”. Everything downstream depends on that sentence existing.',
        'Attach a number and an owner to each candidate. A use case without a named business owner is a hobby, and hobbies do not survive budget season.',
        'Measure the baseline before you build anything. Without it you can never prove the outcome moved, and a CFO will discount an unproven claim to zero.',
        'Run your outcomes as a portfolio rather than a queue: re-rank quarterly against real evidence, and be willing to kill the ones that are not moving.',
        'You are at the top of this dimension. The live risk is portfolio sprawl, so protect the discipline of stopping things, not just starting them.',
      ],
    },
    {
      id: 'trust',
      side: 'hub',
      title: 'Delegation & Trust',
      sub: 'What is the business authorised to ask of the machine?',
      intro: 'The delegation boundary is the line between what stays deterministic code and what is handed to model judgement. Someone has to draw it, and it should not be an engineer, alone, at midnight.',
      questions: [
        {
          component: 'The delegation boundary',
          text: 'Agentic systems mix deterministic code with model judgement. Who has drawn the line between the two?',
          opts: [
            'We haven’t discussed the distinction',
            'Engineers decide case by case; nothing is written down',
            'A documented principle exists and is applied to new builds',
            'The boundary is set per use case and signed off by the business owner',
            'It is enforced policy in the platform, reviewed when models or risk change',
          ],
        },
        {
          component: 'Human authority and decision rights',
          text: 'An agent takes an action that turns out to be wrong and costs real money. What happens?',
          opts: [
            'We don’t know; it hasn’t come up',
            'Someone investigates informally; accountability is unclear',
            'A named human owner is accountable and the incident path is documented',
            'Approval gates and escalation paths were defined in advance for that class of action',
            'Decision rights are formally delegated per action, gates are enforced in the runtime, and every action is reconstructable',
          ],
        },
        {
          component: 'Agent identity and audit trail',
          text: 'How do your agents authenticate into systems, and what trace do they leave?',
          opts: [
            'Haven’t thought about it',
            'They use a shared or borrowed human account; logging is whatever each app happens to give us',
            'Agents have their own credentials; actions are logged system by system',
            'Agents have first-class identity with scoped permissions and a consolidated audit trail',
            'Identity, entitlement and audit are centrally governed, and evidence packs can be produced for auditors or regulators on demand',
          ],
        },
      ],
      next: [
        'Put the delegation boundary on an executive agenda. “What are we willing to let a model decide?” is a business question, and answering it late is how organisations discover their exposure through an incident.',
        'Write the boundary down, on one page. What stays deterministic code, what is delegated to model judgement, and the reasoning. Undocumented principles do not survive staff turnover.',
        'Move from a general principle to per-use-case sign-off, with approval gates and escalation paths agreed before go-live rather than reconstructed after the first bad action.',
        'Enforce the boundary in the platform rather than in documents, give agents first-class identity, and make every action reconstructable for someone who was not in the room.',
        'You are at the top of this dimension. Re-test the boundary whenever models change, because rising capability silently widens what you have already authorised.',
      ],
    },
    {
      id: 'change',
      side: 'hub',
      title: 'Change & Capability',
      sub: 'Will humans actually work this way?',
      intro: 'Every agentic programme eventually runs into the organisation. Sponsorship, redesigned work and workforce trust decide whether the technology is adopted or quietly routed around.',
      questions: [
        {
          component: 'Executive ownership and transformation governance',
          text: 'Who owns your agentic AI agenda?',
          opts: [
            'Nobody specifically; it is happening in pockets',
            'IT or a data/AI team, as a technology initiative',
            'A named executive sponsor with a budget',
            'A cross-functional transformation office with mandate over the whole portfolio',
            'Board-level ownership, with a transformation office governing benefits rather than just delivery',
          ],
        },
        {
          component: 'Work and role redesign',
          text: 'When an agent takes over part of a process, what happens to the process and the roles around it?',
          opts: [
            'The question hasn’t arisen',
            'The agent is bolted onto the existing process; nothing else changes',
            'We adjust the process locally, where friction shows up',
            'Roles, handoffs and controls are redesigned around the agent before go-live',
            'Work design is a standing discipline: organisation, process and agents are designed together',
          ],
        },
        {
          component: 'Workforce trust and enablement',
          text: 'How would your workforce describe agentic AI today?',
          opts: [
            'Most people haven’t engaged with it, and we have never asked',
            'Anxiety and rumour; communication has been thin',
            'Cautious interest; we communicate, and training is available to volunteers',
            'Broad engagement, with role-specific enablement, and objections are actively surfaced and answered',
            'People propose and design agents themselves; incentives reward it, and adoption is measured',
          ],
        },
      ],
      next: [
        'Name a single accountable executive. Agentic AI without one owner stays a collection of experiments that each die when their champion changes role.',
        'Move ownership out of IT. For as long as this is a technology initiative, the rest of the business will treat the outcome as optional, and they will be right to.',
        'Give the sponsor a cross-functional transformation office with mandate over the portfolio, and put change management inside it rather than beside it.',
        'Shift the office from governing delivery to governing benefits. Its job is to prove value landed, not that projects closed on time.',
        'You are at the top of this dimension. The binding constraint is now workforce trust, so keep measuring sentiment and adoption, not just delivery.',
      ],
    },
    {
      id: 'knowledge',
      side: 'core',
      title: 'Knowledge & Data',
      sub: 'Can an agent know what it needs to know?',
      intro: 'Agents fail on ambiguity long before they fail on model quality. Data access, one shared vocabulary, and a deliberate memory design are what separate a demo from a colleague.',
      questions: [
        {
          component: 'A governed data foundation',
          text: 'If an agent needed the data behind a core business decision, how would it get it?',
          opts: [
            'It would have to ask a person, who would export a spreadsheet',
            'The data sits in source systems; someone would build a bespoke integration',
            'A governed data platform exists and covers the main domains',
            'Governed platform with catalog, lineage and quality SLAs; agents can be granted scoped access',
            'All of that, plus data products designed for machine consumption with freshness guarantees',
          ],
        },
        {
          component: 'Semantics and ontology: the shared contract',
          text: 'If you asked three departments what an “active customer” or an “on-time delivery” is, how many answers would you get?',
          opts: [
            'Three, and nobody has ever tried to reconcile them',
            'Three, and we know it is a problem',
            'One definition exists in a glossary, but systems don’t all follow it',
            'A semantic layer defines entities, metrics and actions, and applications consume it',
            'The ontology is the contract: agents, BI and applications all resolve against it, and changes to it are governed',
          ],
        },
        {
          component: 'Agent memory as a design decision',
          text: 'What does an agent remember between one task and the next?',
          opts: [
            'We haven’t considered this',
            'Nothing; every run starts cold with whatever is in the prompt',
            'Conversation history, within a single session',
            'We deliberately design what persists: what it learns, what it recalls, what it must forget',
            'Memory is architected across working, episodic, procedural and semantic layers, governed for retention and privacy, and it improves with outcomes',
          ],
        },
      ],
      next: [
        'Ask the question out loud in your next architecture review: if an agent needed this data, could it get it without a human in the loop? The answer is usually no, and that is the finding.',
        'Pick the two or three data domains your priority outcomes actually depend on, and govern those first. Enterprise-wide data programmes finish long after your agent roadmap needed them.',
        'Add catalog, lineage and quality SLAs, then define one shared vocabulary for entities, metrics and actions. Ambiguity is the single most common cause of agent failure in production.',
        'Design data products for machine consumption, make the ontology the contract everything resolves against, and then make a deliberate decision about what your agents remember.',
        'You are at the top of this dimension. Govern ontology change like an API change, because a silent redefinition breaks every agent downstream of it.',
      ],
    },
    {
      id: 'systems',
      side: 'core',
      title: 'Systems & Action',
      sub: 'Can it actually do anything?',
      intro: 'An agent that can only read is a search engine. The integration and action gateway, with its registry, broker and scoped grants, is where agentic AI becomes useful and where it becomes dangerous.',
      questions: [
        {
          component: 'Programmatic access to systems of record',
          text: 'How reachable are your core enterprise systems programmatically, whether that is SAP, Oracle, IFS, Workday, Salesforce or an equivalent?',
          opts: [
            'Unclear; we would have to ask the vendor or our systems integrator',
            'Heavily customised, older versions; access is mostly through the UI or batch files',
            'Modern APIs exist for the main systems, granted project by project',
            'A stable API and event layer over the core estate, with clear ownership',
            'Systems of record are exposed as governed, versioned services designed for agent consumption',
          ],
        },
        {
          component: 'A governed tool surface',
          text: 'How does a new agent get access to a tool or a system?',
          opts: [
            'No mechanism; nothing is in production',
            'A developer wires up a point-to-point integration',
            'Reusable connectors exist, but access is granted ad hoc',
            'A tool registry and broker, with scoped grants, standard protocols and an approval flow',
            'The gateway is the enforced security perimeter: every agent action passes through it, with policy applied per grant',
          ],
        },
        {
          component: 'Action safety and write access',
          text: 'Which of your agents can change something, rather than only read and suggest?',
          opts: [
            'None, and we haven’t discussed what we would allow',
            'None; everything is read-only or human copy-paste, by default rather than by decision',
            'A few, in low-risk areas, behind human approval',
            'Write access is granted by class of action, and it is sandboxed, tested and reversible',
            'Write access is routine and safe: bounded, reversible, monitored, and widened on evidence',
          ],
        },
      ],
      next: [
        'Find out where you actually stand. Take your three most important systems and establish what can be reached programmatically, at what version, carrying how much customisation debt.',
        'Stop building point-to-point. Every bespoke integration is a permission you cannot revoke and a dependency nobody can see on a diagram.',
        'Put a registry and broker in front of the estate: scoped grants, standard protocols, one approval flow. This, not the model, is your real security perimeter.',
        'Move deliberately from read to write: grant by class of action, sandbox it, make it reversible, and widen on evidence rather than on enthusiasm.',
        'You are at the top of this dimension. The gateway only protects you if nothing bypasses it, so audit periodically for side channels.',
      ],
    },
    {
      id: 'operate',
      side: 'core',
      title: 'Operate & Prove',
      sub: 'Can you run it, trust it, afford it, and scale it?',
      intro: 'Agents degrade quietly. Without evaluation, observability and a known cost per outcome, you cannot tell a system that is working from one that stopped working four months ago.',
      questions: [
        {
          component: 'Production runtime and reliability',
          text: 'What agentic work is running in production today, with real users depending on it?',
          opts: [
            'Nothing yet; we are reading and discussing',
            'Demos and pilots that don’t survive contact with real volume',
            'One or two things in production, supported informally',
            'Several in production with SLAs, monitoring and an on-call owner',
            'Agents are a supported enterprise capability, with release, versioning and rollback discipline',
          ],
        },
        {
          component: 'Evaluation and observability',
          text: 'An agent worked well at launch. How would you know if it got worse?',
          opts: [
            'We would find out when someone complained',
            'Spot checks by the team that built it',
            'Defined quality metrics, reviewed periodically',
            'Automated evaluation on every change, with regression suites and drift alerts',
            'Evaluation runs continuously and its findings feed back into memory, semantics and policy',
          ],
        },
        {
          component: 'Unit economics: cost per outcome',
          text: 'What does one unit of agentic output cost you?',
          opts: [
            'No idea, and we have never asked',
            'We see a total AI bill but cannot attribute it to anything',
            'Cost is tracked per application or per team',
            'Cost per outcome is known and compared against the value that outcome delivers',
            'Unit economics are actively engineered through routing, caching and batching, and used to decide what scales',
          ],
        },
      ],
      next: [
        'Get one thing into production with a real user depending on it. Pilots teach you almost nothing about what breaks, because nothing is at stake when they break.',
        'Decide what “good” means for each agent before it launches, and how you would detect it degrading. Agents fail silently and politely.',
        'Automate evaluation on every change and start attributing cost. An agent whose unit cost you do not know cannot be scaled responsibly, only hopefully.',
        'Close the loop: let outcome evidence feed back into memory, semantics and policy, and engineer unit economics deliberately through routing, caching and batching.',
        'You are at the top of this dimension. Your advantage is now the speed of that feedback loop, so protect it from process.',
      ],
    },
  ],
  archetypes: {
    ambition: {
      name: 'Ambition-Led',
      short: 'You are authorising more than your Core can safely deliver.',
      long: 'Your business has done the harder half of the thinking: there is intent, there is ownership, and someone can describe the value in a sentence. What is missing is the machinery underneath: the data an agent could actually reach, the governed surface it would act through, and the evidence loop that would tell you whether it still works next quarter. This is the most common position, and it is also the specific reason agentic pilots stall between proof of concept and production. The constraint is not ambition and it is not the model. It is Agent Core.',
      move: 'Do not add use cases. Build the Core once, against the two or three outcomes you have already named.',
    },
    capability: {
      name: 'Capability-Led',
      short: 'You have built more than the business has decided how to use.',
      long: 'Your engineering organisation is ahead of your executive team. The platform, the data, and in some cases running agents already exist, but the business has not defined what it wants delegated, who is accountable when an agent is wrong, or which outcome any of this is supposed to move. Value is stranded in the platform. This position is less common than the reverse and it is more frustrating, because the investment has already been made. What is missing costs far less than what you have already built.',
      move: 'Run a delegation and value workshop with the executive team before building anything further.',
    },
    observer: {
      name: 'Observer',
      short: 'Agentic AI is a topic in your organisation, not yet a capability.',
      long: 'Neither side of the model is in place, which is a more honest and more common position than most public conversation suggests. The advantage is that you have not yet spent money on the wrong sequence: many organisations ahead of you on activity are not ahead of you on readiness, because they built pilots before they built a foundation. The cheapest move available to you is deciding what business value you are actually chasing, before any technology decision is taken.',
      move: 'Start with the outcome, not the tooling. One named, measured, owned business outcome.',
    },
    compounding: {
      name: 'Compounding',
      short: 'Both sides move together. Your constraint is pace, not readiness.',
      long: 'Authority and capability are advancing in step, which is rare and is the condition under which agentic investment actually compounds: each outcome delivered makes the next one cheaper, because the Core is shared and the governance already exists. Your risk profile changes at this point. The threats are no longer technical readiness but portfolio sprawl, ontology drift, and an authorised surface that widens faster than anyone re-examines it.',
      move: 'Govern the portfolio and re-test the delegation boundary on a fixed cadence.',
    },
  },
  solutions: {
    strategy: {
      nm: 'Strategy Consulting: DAIMI / AIMI',
      ds: 'Maturity assessment turned into a prioritised, quantified transformation roadmap. Powered by the Discovery Platform.',
    },
    tmo: {
      nm: 'Transformation Management',
      ds: 'A transformation office that governs benefits, not just delivery, with change management and culture at the centre. Powered by the Navigator Platform.',
    },
    enterprise: {
      nm: 'Enterprise AI Solutions',
      ds: 'Agent Core as enterprise-grade agentic infrastructure, and Agent Hub as the design and run environment, built for SAP, Oracle, IFS, Workday and Salesforce estates.',
    },
  },
  routing: {
    value: 'strategy',
    change: 'tmo',
    trust: 'enterprise',
    knowledge: 'enterprise',
    systems: 'enterprise',
    operate: 'enterprise',
  },
}
